
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { Component, ObjectManager } from '../../../concepts/objects';
import { Point } from '../../../platform/geom/Point';
import { AdapterInfo } from '../../canvas/AdapterInfo';
import { Camera, CameraState } from '../../canvas/Camera';
import { EditorSession } from '../../canvas/EditorSession';
import { Phys } from '../../canvas/entities/Phys';
import { RelativeTo } from '../../canvas/entities/Physical';
import { MyRoleInfo } from '../../canvas/MyRoleInfo';
import { SpatialIndex } from '../../canvas/utilities/SpatialIndex';
import { WidgetManager } from '../../canvas/workspace/widgets/WidgetManager';
import { ActiveObject, resolve } from '../../Container';
import { autoLayout } from '../autoLayout/AutoLayoutCommands';
import { Frame } from './Frame';
import { GridAdapter } from './FrameAdapter';
import { FrameAddon } from './FrameAddon';
import { FrameBorderWidthAttr, FrameCameraScaleAttr, FrameLabelFontSizeAttr, FrameUserElevatedAttr } from './FrameAssociations';

export class FrameMechanics extends ActiveObject {

    @resolve(Camera) private camera: Camera;
    @resolve(CommandDispatcher) private dispatch: CommandDispatcher;
    @resolve(ObjectManager) private objects: ObjectManager;
    @resolve(EditorSession) private session: EditorSession;
    @resolve(SpatialIndex) private spatial: SpatialIndex;
    @resolve(WidgetManager) private widgets: WidgetManager;

    public start(): void {
        this.burden(
            this.camera.observe(this.updateFrameBordersOnZoomChange),
            this.session.observe(AdapterInfo, this.realizeFrameAdapter),
            this.session.observe(MyRoleInfo, () => this.updateElevation()),
            this.objects.on('Frame', 'allocated', ([x]) => this.updateElevation(x)),
            this.objects.on('Frame', 'affected', () => this.updateScaling(this.camera.scale)),
            // So because frames are proxying their autoLayout behavior onto the addon, we need to support the the autoLayout trigger
            // in here because the AutoLayoutMechanics won't know about this.
            this.objects.createTrigger('FrameAddon.LayoutModeChanged')
                .for(FrameAddon)
                .onChangesTo('layoutMode' as any)
                .doForEach(x => this.dispatch(autoLayout, [x?.parentFrame?.id, true])),
            this.objects.createTrigger('Frame.Consume')
                .for(Frame)
                .onManipulated('translate', 'scale')
                .doForEach(x => this.consumeVisibleElements(x))
        );

        this.updateElevation();
        this.updateScaling(this.camera.scale);
        this.realizeFrameAdapter();
    }

    private consumeVisibleElements = (frame: Frame) => {
        if (!!frame.background) return;
        // Find any head elements that are behind this frame...
        const bounds = frame.bounds(RelativeTo.Model);
        const hits = Phys.from(this.spatial.lookup(bounds))
            .takeHeads()
            .whereBehind(frame)
            .whereContainedIn(bounds)
            .orderFrontToBack()
            .toArray();
        for (const elmt of hits) {
            if (elmt === frame) continue;
            const figure = elmt.createFigure();
            frame.addAtBack(elmt.removeSelf());
            elmt.setFromFigure(figure);
        }
    };

    private updateFrameBordersOnZoomChange = (next: CameraState, prev: CameraState) => {
        if (next.scale === prev.scale) return;
        this.updateScaling(next.scale);
    };

    private updateElevation = (frame?: Component) => {
        const myRole = this.session.get(MyRoleInfo);
        const frames = frame ? [frame.as(Frame)].filter(x => !!x) : this.objects.getAll(Frame);
        this.objects.update(() => {
            for (const f of frames) {
                FrameUserElevatedAttr.setMaybe(f, myRole.canManage);
            }
        });
    };

    private updateScaling = (scale: number) => {
        const frames = this.objects.getAll(Frame);

        const fontBase = 12;
        const fontMin = fontBase;

        for (const f of frames) {
            FrameCameraScaleAttr.setMaybe(f, scale);
            FrameLabelFontSizeAttr.setMaybe(f, Math.max(fontBase / scale, fontMin));
            FrameBorderWidthAttr.setMaybe(f, 1 / scale);
        }
    };

    private realizeFrameAdapter = (next?: AdapterInfo) => {
        next = next ?? this.session.get(AdapterInfo);

        this.widgets.delete('frameGrid');
        if (next?.type !== 'frame') return;

        const mount = this.widgets.createContainer({
            name: 'frameGrid',
            position: ['absolute', { at: Point.zero, relativeTo: 'view' }],
            style: 'none',
        });

        mount.add(this.widgets.createInline({
            ui: GridAdapter,
        }));

        this.widgets.add(mount);
    };
}
